import { useEffect, useState } from "react";
import { generateOtp, validateOtp } from "@services/authenticationService";
import { environment } from "@environments/environment";
import { useLoginGateway } from "@hooks/store";
import { useAuthSession } from "./useAuthSession";
import { useInvisibleRecaptcha } from "./useInvisibleRecaptcha";
import { useOtpTimer } from "./useOtpTimer";
import {
  buildGenerateOtpPayload,
  buildOtpValidatePayload,
} from "./loginPayloads";
import type { PhoneCredentials } from "./types";

type UseOtpLoginParams = {
  mobileNumber: string;
  isdCode: string;
  /** Admission flow only — omitted for mobile login */
  admissionNo?: string;
  /** Auto-send OTP on mount (admission inline OTP step) */
  executeOnMount?: boolean;
  /** Start resend countdown on mount (mobile VerifyOtp modal after SendOtp) */
  startTimerOnMount?: boolean;
  enabled?: boolean;
  onAuthenticated?: () => void;
  phone?: PhoneCredentials;
};

export function useOtpLogin({
  mobileNumber,
  isdCode,
  admissionNo,
  executeOnMount = false,
  startTimerOnMount = false,
  enabled = true,
  onAuthenticated,
  phone,
}: UseOtpLoginParams) {
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const loginGateway = useLoginGateway();
  const { completeLogin } = useAuthSession({ onAuthenticated });
  const { timeLeft, canResend, startTimer, formatTime } = useOtpTimer();

  const phoneCredentials: PhoneCredentials = phone ?? {
    dialCode: isdCode,
    number: mobileNumber,
  };

  useEffect(() => {
    if (startTimerOnMount) {
      startTimer(60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTimerOnMount]);

  const sendOtp = async (captchaToken: string, resetCaptcha: () => void) => {
    const payload = buildGenerateOtpPayload(
      phoneCredentials,
      captchaToken,
      admissionNo
    );

    try {
      const res = await generateOtp(payload, loginGateway);
      if (res) {
        resetCaptcha();
        setError("");
        startTimer(60);
      }
    } catch (err: unknown) {
      resetCaptcha();
      const message =
        (err as { detail?: string; message?: string })?.detail ||
        (err as { message?: string })?.message ||
        "OTP limit reached. Please try again after 1 hour";
      setError(message);
    }
  };

  const { captchaRef, executeCaptcha, resetCaptcha } = useInvisibleRecaptcha({
    siteKey: environment.captchaSiteKey,
    onToken: (token) => sendOtp(token, resetCaptcha),
    executeOnMount,
    enabled,
  });

  const verifyOtp = async (otp: string) => {
    setIsVerifying(true);
    try {
      const res = await validateOtp(
        buildOtpValidatePayload(mobileNumber, otp, isdCode, admissionNo),
        loginGateway
      );

      if (res?.accessToken) {
        const result = await completeLogin(res.accessToken);
        if (!result.success) {
          setError(result.error);
        }
      } else {
        setError(
          (res as { detail?: string })?.detail || "Incorrect OTP! Try again"
        );
      }
    } catch (err: unknown) {
      const message =
        (err as { detail?: string; message?: string })?.detail ||
        (err as { message?: string })?.message ||
        "Incorrect OTP! Try again";
      setError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const resendOtp = () => executeCaptcha();

  return {
    error,
    setError,
    isVerifying,
    verifyOtp,
    resendOtp,
    captchaRef,
    timeLeft,
    canResend,
    formatTime,
  };
}
